import type { DayReport } from "../types/api";
import {
  formatNoteTime,
  groupReportNotes,
  noteTypeMeta,
  type ReportNoteView,
} from "../lib/reportNotes";
import "./report-notes.css";

function NoteCard({ note }: { note: ReportNoteView }) {
  const meta = noteTypeMeta(note.noteType);
  const time = formatNoteTime(note.occurredAt);
  return (
    <article className={`report-note-card report-note-card--${meta.className}`}>
      <div className="report-note-card__meta">
        <span className="badge">{meta.label}</span>
        {time && <span className="muted">{time}</span>}
        {note.requiresFollowUp && <span className="badge badge-in_progress">Opvolging nodig</span>}
      </div>
      <p>{note.noteText}</p>
    </article>
  );
}

export function ReportNotesSection({ report }: { report: DayReport }) {
  const groups = groupReportNotes(report);
  const hasContent = groups.linked.length > 0 || groups.general.length > 0 ||
    groups.legacyParagraphs.length > 0;

  return (
    <div className="section report-notes-section">
      <h2>Notities en bijzonderheden</h2>
      {!hasContent && <p className="empty-state">Geen bijzonderheden geregistreerd</p>}

      {groups.linked.map((group) => (
        <section
          className="report-note-group"
          key={group.workAssignmentId}
          aria-label={`Notities bij ${group.description}`}
        >
          <h3>
            {group.workPackage && <span className="badge badge-wp">{group.workPackage}</span>}{" "}
            {group.description}
          </h3>
          <div className="report-note-list">
            {group.notes.map((note, index) => (
              <NoteCard
                key={note.reportNoteId ?? `${group.workAssignmentId}-${index}`}
                note={note}
              />
            ))}
          </div>
        </section>
      ))}

      {groups.general.length > 0 && (
        <section className="report-note-group" aria-label="Algemene notities">
          <h3>Algemene notities</h3>
          <div className="report-note-list">
            {groups.general.map((note, index) => (
              <NoteCard key={note.reportNoteId ?? `general-${index}`} note={note} />
            ))}
          </div>
        </section>
      )}

      {groups.legacyParagraphs.length > 0 && (
        <section className="report-note-group report-note-group--legacy">
          <h3>Historische algemene notitie</h3>
          <div className="report-note-list">
            {groups.legacyParagraphs.map((paragraph, index) => (
              <article className="report-note-card" key={index}>
                <p>{paragraph}</p>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
